
import { createContext, useContext, useReducer, ReactNode, useMemo } from "react"

type AppState = {
  activeChatbotId: string | null
  darkMode: boolean
  sidebarOpen: boolean
}

type AppAction = 
  | { type: 'SET_ACTIVE_CHATBOT', payload: string | null }
  | { type: 'TOGGLE_DARK_MODE' }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_SIDEBAR', payload: boolean }

const initialState: AppState = {
  activeChatbotId: null,
  darkMode: false,
  sidebarOpen: true
}

// Reducer for handling app state changes
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_ACTIVE_CHATBOT':
      return { ...state, activeChatbotId: action.payload }
    case 'TOGGLE_DARK_MODE':
      return { ...state, darkMode: !state.darkMode }
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarOpen: !state.sidebarOpen }
    case 'SET_SIDEBAR':
      return { ...state, sidebarOpen: action.payload }
    default:
      return state
  }
}

// Create context
const AppContext = createContext<{
  state: AppState
  dispatch: React.Dispatch<AppAction>
}>({
  state: initialState,
  dispatch: () => null
})

// Provider component
export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState)
  const value = useMemo(() => ({ state, dispatch }), [state])
  
  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  )
}

// Hook for using the app context
export function useAppContext() {
  const context = useContext(AppContext)
  
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider')
  }
  
  return context
}
